import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import * as Sentry from "@sentry/react";
import { useAction, useMutation as useConvexMutation } from "convex/react";
import { useDialogA11y } from "@/lib/useDialogA11y";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import Icon from "./Icon";
import { GlassCard, Btn } from "./UI";
import {
  useStoreMutation,
  mutations,
  type RecipeSeasonTag,
  type RecipeIngredient,
} from "@/lib/store";
import { parseRecipeText } from "@/lib/recipeParser";
import { api } from "../../../../convex/_generated/api";

/**
 * AddRecipeSheet
 *
 * Sister-facing entry surface for new recipes. Two tabs:
 *
 *   import  Paste a recipe URL. We mock-parse it and prefill a draft. The
 *           sister can edit before saving. Mock fixtures cycle by URL hash.
 *   write   Blank slate; sister writes title, description, time, macros,
 *           ingredients, steps. Optional public toggle and season tags.
 *
 * On save, the new recipe is added to the store and we navigate to the new
 * detail page so she can verify what she just made.
 */

type Tab = "capture" | "import" | "write";

/**
 * Pull the first http(s) URL out of free-form pasted text. Used by the
 * Capture tab to detect when a paste came from iOS Share Sheet (which
 * sandwiches a TikTok/IG link inside an auto-generated caption like
 * "Check out this TikTok I found! ... https://www.tiktok.com/t/...").
 * Returns null when no URL is present.
 */
function extractFirstUrl(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s<>"]+/);
  if (!match) return null;
  // Strip trailing punctuation that pasted along with the URL.
  return match[0].replace(/[.,;:!?)\]}>]+$/, "");
}

const SEASONS: { value: RecipeSeasonTag; label: string }[] = [
  { value: "quick",      label: "Quick" },
  { value: "family",     label: "Family" },
  { value: "pregnant",   label: "Pregnant" },
  { value: "postpartum", label: "Postpartum" },
  { value: "nursing",    label: "Nursing" },
];

export default function AddRecipeSheet({
  open,
  onClose,
  authorId,
  initialTab = "capture",
}: {
  open: boolean;
  onClose: () => void;
  authorId: string;
  initialTab?: Tab;
}) {
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState<Tab>(initialTab);

  // Sync the active tab with initialTab whenever the sheet reopens.
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  // Capture (Recime-style) tab state
  const [pasted, setPasted] = useState<string>("");
  const [captureUrl, setCaptureUrl] = useState<string>("");
  const [captureError, setCaptureError] = useState<string>("");
  const [capturing, setCapturing] = useState<boolean>(false);
  const captureRecipe = useConvexMutation(api.recipesDb.captureRecipe);
  const parseTextAction = useAction(api.recipes.parseText);
  const populateNutrition = useAction(api.recipesDb.populateNutrition);

  // Import tab state
  const [url, setUrl] = useState<string>("");
  const parseUrlAction = useAction(api.recipes.parseUrl);
  const [importError, setImportError] = useState<string>("");
  const [importing, setImporting] = useState<boolean>(false);

  // Write tab state
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [durationMin, setDurationMin] = useState<number>(20);
  const [servings, setServings] = useState<number>(4);
  const [kcal, setKcal] = useState<number>(420);
  const [proteinG, setProteinG] = useState<number>(28);
  const [carbsG, setCarbsG] = useState<number>(40);
  const [fatG, setFatG] = useState<number>(14);
  const [fiberG, setFiberG] = useState<number>(8);
  const [seasonTags, setSeasonTags] = useState<RecipeSeasonTag[]>(["family"]);
  const [ingredientsText, setIngredientsText] = useState<string>("1 cup rolled oats\n1 cup milk\n2 tbsp honey");
  const [stepsText, setStepsText] = useState<string>("Mix all ingredients\nLet sit overnight\nServe cold");
  const [isPublic, setIsPublic] = useState<boolean>(false);
  // Rendered in the sheet when the write fails. The sheet stays open holding
  // everything she typed, so a lost save can never look like a saved one.
  const [createError, setCreateError] = useState<string>("");
  const createRecipe = useConvexMutation(api.recipesDb.createRecipe);

  const titleId = "addrecipe-title";
  const { containerRef } = useDialogA11y({ open, onClose, titleId });

  if (!open) return null;

  /**
   * Backfill macros for a recipe that is ALREADY saved.
   *
   * A failure here is not a failed save, so it never blocks or undoes one. But
   * the recipe would then render 0 kcal / 0g protein with no explanation, and
   * in a nutrition app unexplained zeros read as measured values — "this food
   * has no calories". So she is told the numbers are missing, not wrong, and
   * handed a retry.
   */
  const backfillNutrition: (recipeId: string) => void = (recipeId) => {
    void populateNutrition({ recipeId }).catch((err: unknown) => {
      console.error("[AddRecipeSheet] nutrition backfill failed", err);
      Sentry.captureException(err, { tags: { subsystem: "add-recipe-sheet" } });
      toast({
        variant: "destructive",
        title: "Nutrition unavailable",
        description:
          "Your recipe is saved and safe. We just couldn't add up its nutrition, so any zeros you see on it aren't real numbers yet.",
        action: (
          <ToastAction
            altText="Try adding up the nutrition again"
            onClick={() => backfillNutrition(recipeId)}
          >
            Retry
          </ToastAction>
        ),
      });
    });
  };

  const onImport = async () => {
    if (importing) return; // double-tap guard
    setImportError("");
    const trimmed = url.trim();
    if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
      setImportError("Paste a full link starting with http or https.");
      return;
    }
    setImporting(true);
    try {
      const result = await parseUrlAction({ url: trimmed });
      const { id } = await captureRecipe({
        authorId,
        parsed: result.recipe,
        sourceUrl: trimmed,
      });
      // Fire-and-forget USDA nutrition lookup. Macros populate via the
      // reactive query on the detail page within a few seconds.
      backfillNutrition(id);
      onClose();
      setLocation(`/nourish/${id}`);
    } catch (err) {
      // ConvexError surfaces typed codes via err.data.code so the UI can
      // route the user to the right next step instead of silently faking
      // a recipe.
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === "RECIPE_URL_UNREADABLE") {
        // Site blocked our fetch (Instagram/TikTok login walls, 4xx, timeout).
        // Auto-switch to Capture tab with the URL prefilled — Per spec choice C.
        setCaptureUrl(trimmed);
        setCaptureError(
          "We couldn't read this link directly — the site blocks automated readers. " +
            "Open the post, copy the caption text, and paste it below.",
        );
        setUrl("");
        setTab("capture");
      } else if (code === "RECIPE_NO_RECIPE_FOUND") {
        setImportError(
          "We couldn't find a recipe on that page. Try the Capture tab and paste the text directly.",
        );
      } else if (code === "RECIPE_URL_INVALID") {
        setImportError("URL must start with http:// or https://");
      } else {
        console.error("[AddRecipeSheet] recipe import from URL failed", err);
        Sentry.captureException(err, { tags: { subsystem: "add-recipe-sheet" } });
        setImportError("Something went wrong reading that link. Try again or use the Capture tab.");
      }
    } finally {
      setImporting(false);
    }
  };

  const onCapture = async () => {
    if (capturing) return; // double-tap guard
    setCaptureError("");
    const text = pasted.trim();
    if (text.length < 20) {
      setCaptureError("Paste at least a few lines of recipe text so we can read it.");
      return;
    }
    setCapturing(true);
    try {
      // Three-tier extraction matching the spec:
      //   1. Pasted text contains a URL → route through parseUrl
      //      (Apify for IG/TikTok, JSON-LD/AI for blogs)
      //   2. Local heuristic on the text — works for header-friendly blog text
      //   3. Server parseText action — heuristic + AI fallback
      const embeddedUrl = extractFirstUrl(text);
      const sourceUrl = captureUrl.trim() || embeddedUrl || null;

      // 1. URL path
      if (embeddedUrl) {
        try {
          const result = await parseUrlAction({ url: embeddedUrl });
          const { id } = await captureRecipe({
            authorId,
            parsed: result.recipe,
            sourceUrl,
          });
          backfillNutrition(id);
          onClose();
          setLocation(`/nourish/${id}`);
          return;
        } catch (urlErr) {
          // URL fetch failed (dead link, blocked, no-recipe in post).
          // Fall through to text parsing — the surrounding text might
          // still have the recipe (e.g. a paste with both URL + caption).
          // Capture so we know how often the URL path fails silently.
          console.error("[AddRecipeSheet] URL parse failed, falling back to text", urlErr);
          Sentry.captureException(urlErr, { tags: { subsystem: "add-recipe-sheet" } });
        }
      }

      // 2. Local heuristic (instant, no network)
      const heuristic = parseRecipeText(text);
      if (heuristic.ingredients.length >= 2) {
        const { id } = await captureRecipe({
          authorId,
          parsed: heuristic,
          sourceUrl,
        });
        backfillNutrition(id);
        onClose();
        setLocation(`/nourish/${id}`);
        return;
      }

      // 3. Server parseText (AI fallback)
      const result = await parseTextAction({
        text,
        sourceUrl: sourceUrl ?? undefined,
      });
      const { id } = await captureRecipe({
        authorId,
        parsed: result.recipe,
        sourceUrl,
      });
      backfillNutrition(id);
      onClose();
      setLocation(`/nourish/${id}`);
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === "RECIPE_NO_RECIPE_FOUND") {
        setCaptureError(
          "We couldn't read a recipe from that text. Try pasting more, or use the Link tab if you have a URL.",
        );
      } else {
        console.error("[AddRecipeSheet] recipe capture from text failed", err);
        Sentry.captureException(err, { tags: { subsystem: "add-recipe-sheet" } });
        setCaptureError("Something went wrong reading that text. Try again or use the Link tab.");
      }
    } finally {
      setCapturing(false);
    }
  };

  const toggleSeason = (s: RecipeSeasonTag) => {
    setSeasonTags((prev) =>
      prev.includes(s) ? prev.filter((t) => t !== s) : [...prev, s],
    );
  };

  const onCreate = async () => {
    if (!title.trim() || !description.trim()) return;
    setCreateError("");
    const ingredients: RecipeIngredient[] = ingredientsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        // Naive parser: "1 cup oats" -> qty=1, unit=cup, item=oats
        const m = line.match(/^([\d./]+)\s+(\S+)\s+(.+)$/);
        if (m) {
          const q = m[1].includes("/")
            ? Number(m[1].split("/")[0]) / Number(m[1].split("/")[1])
            : Number(m[1]);
          return { qty: isFinite(q) ? q : 1, unit: m[2], item: m[3] };
        }
        return { qty: 1, unit: "", item: line };
      });
    const steps = stepsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line, i) => ({
        title: `Step ${i + 1}`,
        body: line,
      }));
    try {
      const { id } = await createRecipe({
        authorId,
        title: title.trim(),
        description: description.trim(),
        servings,
        durationMin,
        kcal, proteinG, carbsG, fatG, fiberG,
        ingredients,
        steps,
        seasonTags,
        scripturePairing: null,
        isPublic,
      });
      onClose();
      setLocation(`/nourish/${id}`);
    } catch (err) {
      console.error("[AddRecipeSheet] createRecipe mutation failed", err);
      Sentry.captureException(err, { tags: { subsystem: "add-recipe-sheet" } });
      // Sheet stays open with every field intact — nothing was saved and
      // nothing should look like it was.
      setCreateError(
        "We couldn't plant that recipe, so nothing was saved. Everything you wrote is still right here — check your connection and tap Plant this recipe again.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="add-recipe-overlay"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "88vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Plant a recipe</p>
            <h2 id={titleId} className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
              Add to your garden
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-add-recipe"
            className="hover-elevate"
            style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-4 mnf-body"
          style={{ background: "rgba(30,74,53,0.06)", borderRadius: 12, padding: 4 }}
          role="tablist"
        >
          <TabButton testId="tab-capture" label="Capture" active={tab === "capture"} onClick={() => setTab("capture")} />
          <TabButton testId="tab-import"  label="Link"    active={tab === "import"}  onClick={() => setTab("import")} />
          <TabButton testId="tab-write"   label="Write"   active={tab === "write"}   onClick={() => setTab("write")} />
        </div>

        {tab === "capture" ? (
          <GlassCard padding={16}>
            <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Paste a recipe you love
            </p>
            <p className="mnf-body mt-1.5" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              Found a recipe on Instagram, TikTok, or a blog? Tap share, copy, then paste it below. We'll read the ingredients and steps for you.
            </p>
            <textarea
              data-testid="input-capture-text"
              aria-label="Recipe text to capture (paste a caption or page text)"
              placeholder="Paste the full caption or page text here..."
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              className="mnf-body w-full mt-4"
              rows={9}
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid rgba(30,74,53,0.18)",
                background: "rgba(255,255,255,0.65)",
                color: "var(--mnf-fd)",
                fontSize: 13,
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                lineHeight: 1.5,
              }}
            />
            <input
              data-testid="input-capture-source"
              aria-label="Source link for this recipe (optional)"
              type="url"
              placeholder="Source link (optional)"
              value={captureUrl}
              onChange={(e) => setCaptureUrl(e.target.value)}
              className="mnf-body w-full mt-2"
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(30,74,53,0.18)",
                background: "rgba(255,255,255,0.65)",
                color: "var(--mnf-fd)",
                fontSize: 12.5,
                outline: "none",
              }}
            />
            {captureError && (
              <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-t)", marginTop: 8 }}>
                {captureError}
              </p>
            )}
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 10 }}>
              You can edit the title, ingredients, and steps after we read it.
            </p>
            <div className="mt-4">
              <Btn
                data-testid="button-do-capture"
                variant="primary"
                block
                size="lg"
                onClick={onCapture}
                disabled={capturing}
              >
                <Icon name="auto_awesome" size={18} /> {capturing ? "Reading..." : "Read this recipe"}
              </Btn>
            </div>
          </GlassCard>
        ) : tab === "import" ? (
          <GlassCard padding={16}>
            <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Paste a recipe link
            </p>
            <p className="mnf-body mt-1.5" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              We'll fetch the title, time, and ingredients into a clean draft. You can edit before saving.
            </p>
            <input
              data-testid="input-import-url"
              aria-label="Recipe URL to import"
              type="url"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mnf-body w-full mt-4"
              style={{
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid rgba(30,74,53,0.18)",
                background: "rgba(255,255,255,0.65)",
                color: "var(--mnf-fd)",
                fontSize: 13,
                outline: "none",
              }}
            />
            {importError && (
              <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-t)", marginTop: 8 }}>
                {importError}
              </p>
            )}
            <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 10 }}>
              We read the page directly when we can. Sites that block bots fall back to a quick best-guess draft you can edit.
            </p>
            <div className="mt-4">
              <Btn data-testid="button-do-import" variant="primary" block size="lg" onClick={onImport} disabled={importing}>
                <Icon name="link" size={18} /> {importing ? "Reading..." : "Pull this recipe in"}
              </Btn>
            </div>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            <GlassCard padding={16}>
              <Field label="Title">
                <input
                  data-testid="input-title"
                  aria-label="Recipe title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Honey peach overnight oats"
                  className="mnf-body w-full"
                  style={inputStyle}
                />
              </Field>
              <Field label="Description">
                <textarea
                  data-testid="input-description"
                  aria-label="Recipe description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="One paragraph on why you love this."
                  className="mnf-body w-full"
                  rows={2}
                  style={{ ...inputStyle, resize: "none" }}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <NumberField label="Time (min)" value={durationMin} onChange={setDurationMin} testId="input-duration" />
                <NumberField label="Servings" value={servings} onChange={setServings} testId="input-servings" />
              </div>
            </GlassCard>

            <GlassCard padding={16}>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 10 }}>Macros per serving</p>
              <div className="grid grid-cols-2 gap-3">
                <NumberField label="kcal"     value={kcal}     onChange={setKcal}     testId="input-kcal" />
                <NumberField label="Protein g" value={proteinG} onChange={setProteinG} testId="input-protein" />
                <NumberField label="Carbs g"  value={carbsG}   onChange={setCarbsG}   testId="input-carbs" />
                <NumberField label="Fat g"    value={fatG}     onChange={setFatG}     testId="input-fat" />
                <NumberField label="Fiber g"  value={fiberG}   onChange={setFiberG}   testId="input-fiber" />
              </div>
            </GlassCard>

            <GlassCard padding={16}>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", marginBottom: 10 }}>Season tags</p>
              <div className="flex flex-wrap gap-2">
                {SEASONS.map((s) => {
                  const active = seasonTags.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      data-testid={`button-season-${s.value}`}
                      onClick={() => toggleSeason(s.value)}
                      className="mnf-body hover-elevate"
                      style={{
                        padding: "7px 14px",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                        color: active ? "#F5F0E8" : "var(--mnf-fd)",
                        border: "1px solid transparent",
                        cursor: "pointer",
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </GlassCard>

            <GlassCard padding={16}>
              <Field label="Ingredients (one per line)">
                <textarea
                  data-testid="input-ingredients"
                  aria-label="Recipe ingredients, one per line"
                  value={ingredientsText}
                  onChange={(e) => setIngredientsText(e.target.value)}
                  className="mnf-body w-full"
                  rows={5}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "var(--mnf-mono, monospace)" }}
                />
              </Field>
              <Field label="Steps (one per line)">
                <textarea
                  data-testid="input-steps"
                  aria-label="Recipe steps, one per line"
                  value={stepsText}
                  onChange={(e) => setStepsText(e.target.value)}
                  className="mnf-body w-full"
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </Field>
            </GlassCard>

            <GlassCard padding={16}>
              <button
                data-testid="button-toggle-public"
                onClick={() => setIsPublic(!isPublic)}
                className="flex items-center gap-3 w-full hover-elevate"
                style={{
                  padding: "10px 4px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 36, height: 22, borderRadius: 999,
                    background: isPublic ? "var(--mnf-f)" : "rgba(30,74,53,0.18)",
                    position: "relative", transition: "background 200ms",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      position: "absolute", top: 2,
                      left: isPublic ? 16 : 2,
                      width: 18, height: 18, borderRadius: 999, background: "#fff",
                      transition: "left 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
                    }}
                  />
                </span>
                <div className="flex-1 text-left">
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>
                    Share with the garden
                  </p>
                  <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>
                    Public recipes wait for coach review before they trend.
                  </p>
                </div>
              </button>
            </GlassCard>

            {createError && (
              <p
                role="alert"
                data-testid="create-recipe-error"
                className="mnf-body"
                style={{ fontSize: 12, color: "var(--mnf-t)", lineHeight: 1.5 }}
              >
                {createError}
              </p>
            )}

            <Btn
              data-testid="button-create-recipe"
              variant="primary"
              block
              size="lg"
              onClick={onCreate}
              disabled={!title.trim() || !description.trim()}
            >
              <Icon name="add" size={18} /> Plant this recipe
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid rgba(30,74,53,0.18)",
  background: "rgba(255,255,255,0.65)",
  color: "var(--mnf-fd)",
  fontSize: 13,
  outline: "none",
} as const;

function TabButton({
  testId, label, active, onClick,
}: {
  testId: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      data-testid={testId}
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="flex-1 hover-elevate"
      style={{
        padding: "9px 8px",
        borderRadius: 9,
        background: active ? "var(--mnf-cream)" : "transparent",
        color: active ? "var(--mnf-fd)" : "var(--mnf-tg)",
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        border: "1px solid transparent",
        boxShadow: active ? "0 1px 3px rgba(30,74,53,0.1)" : "none",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block mb-3 last:mb-0">
      <span className="mnf-eyebrow block mb-1" style={{ color: "var(--mnf-tg)" }}>{label}</span>
      {children}
    </label>
  );
}

function NumberField({
  label, value, onChange, testId,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  testId: string;
}) {
  return (
    <label className="block">
      <span className="mnf-eyebrow block mb-1" style={{ color: "var(--mnf-tg)" }}>{label}</span>
      <input
        data-testid={testId}
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mnf-body w-full"
        style={inputStyle}
      />
    </label>
  );
}
